/**
 * useTrialGate.js — client-side hook for the Premium 1-day free-trial
 * gate (Feb 14 2026 spec).
 *
 * Reads `/api/trial/status?feature=X` on mount and re-fires when the
 * caller nudges `refreshKey`. Also exposes a wrapper for handling 402
 * responses from action endpoints — call `handle402(response)` and it
 * returns `true` when it surfaced the paywall so the caller can early-
 * return.
 *
 * Return shape:
 *   { status, loading, expired, refresh, handle402 }
 *   status = { access, kind, seconds_left, trial_expires_at }
 *   expired = boolean quick-flag for `status.kind === "expired"`
 *
 * Features: "learn" | "my_library" | "modules"
 *
 * peek=1 keeps the trial clock cold on mounts that shouldn't count as
 * "first access" (e.g. rendering an inline banner). Default is peek=0
 * so entering the page kicks off the clock — that's the intended UX
 * per the "on first access" answer from the user.
 */
import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { authFetch } from '../utils/authUtils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function useTrialGate(feature, { peek = false, autoStart = true } = {}) {
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const qs = peek || !autoStart ? '&peek=1' : '';
      const r = await authFetch(`${BACKEND_URL}/api/trial/status?feature=${feature}${qs}`);
      if (r.ok) setStatus(await r.json());
    } finally {
      setLoading(false);
    }
  }, [feature, peek, autoStart]);

  useEffect(() => { refresh(); }, [refresh]);

  // Handle a 402 from an action endpoint. Returns true if it triggered
  // the paywall UI (caller should early-return).
  const handle402 = useCallback(async (response) => {
    if (!response || response.status !== 402) return false;
    let body;
    try { body = await response.clone().json(); } catch { body = {}; }
    const err = body?.detail?.error;
    if (err === 'premium_required' || err === 'daily_limit_reached') {
      toast.error(body.detail.message || 'Free trial ended. Upgrade to Premium to continue.', {
        action: {
          label: 'Upgrade',
          onClick: () => navigate('/premium'),
        },
        duration: 8000,
      });
      // Refresh status so UI reflects the expired state.
      refresh();
      return true;
    }
    return false;
  }, [navigate, refresh]);

  return {
    status,
    loading,
    expired: status?.kind === 'expired',
    dailyExhausted: status?.kind === 'daily_exhausted',
    // Consumer-facing "can they act right now" flag — true only when
    // the trial gate would 402. Covers both "come back tomorrow" and
    // "trial over" states for LEARN's daily budget.
    blocked: status?.kind === 'expired' || status?.kind === 'daily_exhausted',
    isPremium: status?.kind === 'premium',
    onTrial: status?.kind === 'trial',
    secondsLeft: status?.seconds_left,
    todayLeft: status?.today_left,
    daysLeft: status?.days_left,
    refresh,
    handle402,
  };
};
